import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CalendarCheck, Inbox, LogOut, RefreshCw, ArrowLeft, ShieldAlert, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LogoMark } from "@/components/LogoMark";
import AdminVisits from "@/components/admin/AdminVisits";
import AdminEnquiries from "@/components/admin/AdminEnquiries";
import { GoogleIcon } from "@/components/GoogleIcon";
import { signInWithGoogle } from "@/components/AuthModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gate = ({ title, body, children, testId }) => (
  <div className="min-h-screen bg-parchment grid place-items-center px-6">
    <div data-testid={testId} className="w-full max-w-md rounded-3xl bg-white border border-sand p-10 text-center">
      <LogoMark className="h-12 w-12 mx-auto" />
      <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Adveshta · Farm Admin</p>
      <h1 className="mt-4 font-serif text-3xl text-pine">{title}</h1>
      <p className="mt-4 text-sm text-sage leading-relaxed">{body}</p>
      <div className="mt-8 grid gap-3">{children}</div>
    </div>
  </div>
);

const Stat = ({ label, value, testId, accent }) => (
  <div data-testid={testId} className={`rounded-3xl border p-6 ${accent ? "bg-pine text-cream border-pine" : "bg-white border-sand"}`}>
    <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${accent ? "text-gold" : "text-sage"}`}>{label}</p>
    <p className={`mt-3 font-serif text-4xl ${accent ? "text-cream" : "text-pine"}`}>{value ?? "—"}</p>
  </div>
);

export default function Admin() {
  const { user, loading, signOut, openAuth } = useAuth();
  const [tab, setTab] = useState("visits");
  const [visits, setVisits] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [overview, setOverview] = useState(null);
  const [fetching, setFetching] = useState(false);

  const load = async () => {
    setFetching(true);
    try {
      const [o, v, e] = await Promise.all([
        axios.get(`${API}/admin/overview`, { withCredentials: true }),
        axios.get(`${API}/admin/visits`, { withCredentials: true }),
        axios.get(`${API}/admin/enquiries`, { withCredentials: true }),
      ]);
      setOverview(o.data);
      setVisits(v.data);
      setEnquiries(e.data);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user]);

  // Keep overview counters in sync with inline status changes.
  useEffect(() => {
    if (!overview) return;
    const today = new Date();
    const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    setOverview((o) => ({
      ...o,
      visits_requested: visits.filter((v) => v.status === "requested").length,
      visits_confirmed_upcoming: visits.filter((v) => v.status === "confirmed" && v.visit_date >= ymd).length,
      enquiries_new: enquiries.filter((e) => (e.status || "new") === "new").length,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visits, enquiries]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-parchment">
        <LogoMark className="h-12 w-12 animate-pulse" />
      </div>
    );
  }

  if (!user) {
    return (
      <Gate
        testId="admin-signin-gate"
        title="Farm team sign-in"
        body="Sign in with the Adveshta Google account to view and manage farm-visit requests and enquiries."
      >
        <button
          data-testid="admin-google-signin-button"
          onClick={signInWithGoogle}
          className="inline-flex items-center justify-center gap-3 rounded-full bg-pine text-cream px-6 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-royal transition-colors duration-300"
        >
          <GoogleIcon /> Sign in with Google
        </button>
        <button
          data-testid="admin-email-signin-button"
          onClick={() => openAuth("login")}
          className="inline-flex items-center justify-center gap-3 rounded-full border border-pine/30 bg-white text-pine px-6 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:border-pine transition-colors duration-300"
        >
          <Mail className="h-4 w-4" /> Sign in with email
        </button>
        <Link to="/" data-testid="admin-back-home-link" className="text-xs text-sage hover:text-pine">
          ← Back to website
        </Link>
      </Gate>
    );
  }

  if (!user.is_admin) {
    return (
      <Gate
        testId="admin-forbidden-gate"
        title="Admin access only"
        body={`${user.email} is not on the farm-team list. Sign in with the Adveshta admin account to continue.`}
      >
        <ShieldAlert className="h-6 w-6 text-gold mx-auto" />
        <button
          data-testid="admin-switch-account-button"
          onClick={async () => {
            await signOut();
            openAuth("login");
          }}
          className="inline-flex items-center justify-center gap-3 rounded-full bg-pine text-cream px-6 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-royal transition-colors duration-300"
        >
          <LogOut className="h-4 w-4" /> Switch account
        </button>
        <Link to="/" data-testid="admin-back-home-link" className="text-xs text-sage hover:text-pine">
          ← Back to website
        </Link>
      </Gate>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-parchment text-coal">
      <header className="sticky top-0 z-40 bg-parchment/85 backdrop-blur-xl border-b border-sand">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Link to="/" data-testid="admin-logo-link"><Logo /></Link>
            <span className="hidden md:inline-flex rounded-full bg-gold/20 border border-gold/40 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-pine">
              Farm Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              disabled={fetching}
              className="inline-flex items-center gap-2 rounded-full border border-sand bg-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-pine hover:border-pine transition-colors duration-300 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${fetching ? "animate-spin" : ""}`} /> Refresh
            </button>
            <div className="hidden sm:flex items-center gap-2 pl-2">
              {user.picture ? (
                <img src={user.picture} alt="" referrerPolicy="no-referrer" className="h-9 w-9 rounded-full ring-2 ring-gold/60 object-cover" />
              ) : (
                <LogoMark className="h-8 w-8" />
              )}
              <span data-testid="admin-user-email" className="text-xs text-sage max-w-[180px] truncate">{user.email}</span>
            </div>
            <button
              data-testid="admin-signout-button"
              onClick={() => signOut()}
              className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-sand bg-white text-pine hover:bg-pine hover:text-cream transition-colors duration-300"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 md:px-12 py-12 lg:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">Khushalpura · Vyara</p>
            <h1 className="mt-4 font-serif font-semibold text-4xl md:text-5xl leading-[1.05] tracking-tight text-pine">
              Farm visits & enquiries
            </h1>
            <p className="mt-4 text-sage text-sm leading-relaxed max-w-xl">
              Confirm a visit once payment is received on the phone — the WhatsApp confirmation
              message to the visitor opens pre-filled from +91 81412 61616.
            </p>
          </div>
          <Link to="/" data-testid="admin-view-site-link" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-sage hover:text-pine">
            <ArrowLeft className="h-3.5 w-3.5" /> View website
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Awaiting confirmation" value={overview?.visits_requested} testId="admin-stat-requested" accent />
          <Stat label="Confirmed · upcoming" value={overview?.visits_confirmed_upcoming} testId="admin-stat-confirmed" />
          <Stat label="New enquiries" value={overview?.enquiries_new} testId="admin-stat-enquiries-new" />
          <Stat label="Total visit requests" value={overview?.visits_total} testId="admin-stat-visits-total" />
        </div>

        <div className="mt-12 flex gap-2 border-b border-sand">
          {[
            { id: "visits", label: "Farm visits", icon: CalendarCheck, count: visits.length },
            { id: "enquiries", label: "Enquiries", icon: Inbox, count: enquiries.length },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`relative inline-flex items-center gap-2 px-5 py-4 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                tab === t.id ? "text-pine" : "text-sage hover:text-pine"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
              <span className="text-gold">{t.count}</span>
              {tab === t.id && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-gold" />}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "visits" ? (
            <AdminVisits visits={visits} setVisits={setVisits} />
          ) : (
            <AdminEnquiries enquiries={enquiries} setEnquiries={setEnquiries} />
          )}
        </div>
      </main>
    </div>
  );
}
